import type {ShippingRuleType} from '@/constants/shippingRuleType';
import type {Acl, ValueOf} from '@myparcel-frontend/types';
import type {ComputedRef} from 'vue';
import {useProposition} from '@myparcel-frontend/proposition';
import {storeToRefs} from 'pinia';
import {computed} from 'vue';
import useAuth from '@/composables/useAuth';
import {SubscriptionFeature} from '@/constants/subscriptionFeatures';
import {useEnvironmentStore} from '@/env/useEnvironmentStore';
import {useAclStore} from '@/store/aclStore/aclStore';
import {parseUrlFromDoubleSlashes} from '@/utils/formatters/url/url';

export default function useAcl() {
  const aclStore = useAclStore();
  const {acl, subscriptionConfiguration} = storeToRefs(aclStore);
  const {environment} = useEnvironmentStore();

  const fetchAcl = async (token?: string): Promise<Acl> => {
    let fetchToken = token;
    if (!fetchToken) {
      const {auth0} = useAuth();
      fetchToken = await auth0.getAccessTokenSilently();
    }

    const response = await fetch(parseUrlFromDoubleSlashes(`${environment.apiUrl}/acl`), {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${fetchToken}`,
        'Content-Type': 'application/json',
      },
    });

    return response.json() as Promise<Acl>;
  };

  const getAcl = async (token?: string) => {
    if (Object.keys(acl.value).length) {
      return acl;
    }

    const fetchedAcl = await fetchAcl(token);
    aclStore.setAcl(fetchedAcl);

    return acl;
  };

  const hasSubscriptionFeature = (feature: string): ComputedRef<boolean> => {
    return computed(() => acl.value.subscription_features.includes(feature));
  };

  const getMaximumShippingRules = (type: ValueOf<typeof ShippingRuleType>) => {
    const {proposition} = useProposition();

    if (
      hasSubscriptionFeature(SubscriptionFeature.UnlimitedShippingRules).value ||
      subscriptionConfiguration.value?.max_shipping_rules_by_type?.length === 0
    ) {
      return Infinity;
    }

    return (
      subscriptionConfiguration.value?.max_shipping_rules_by_type?.[type] ??
      proposition.value.subscriptions.shippingRules.maxAmount
    );
  };

  const hasPermission = (permission: string) => {
    return acl.value.permissions.includes(permission);
  };

  const hasPermissions = (permissions: string[]) => {
    return permissions.every((permission) => hasPermission(permission));
  };

  const getUserId = () => acl.value.user_id;

  return {
    getAcl,
    getUserId,
    hasSubscriptionFeature,
    acl,
    subscriptionConfiguration,
    getMaximumShippingRules,
    hasPermission,
    hasPermissions,
  };
}
